import React, { useState, useEffect, useMemo, Dispatch, SetStateAction } from 'react';
import {
  Button,
  Dropdown,
  Option,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Spinner,
  makeStyles,
  tokens,
  Field,
  Label,
  Card,
  CardHeader,
  Title3,
  CardPreview,
  TabList,
  Tab,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { ReOrderDotsVerticalRegular } from '@fluentui/react-icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Tender,
  TenderingPart,
  Tendering,
  RankingType,
} from '../../../models/tendering';

import { deepCloneObject } from '../../../utils/clone';
import { getTodayKey, toDateKey } from '../../../utils/tendering-utils';

import {
  setTenderingPartRankingByPrice,
  setTenderingPartRankingByRankingValue,
  setTenderListRankings,
  sortByRank,
} from '../../../utils/ranking';



interface TenderingAdminRankingProps {
  tenderingDocument: Tendering,
  tenders: Tender[],
  updateTenderingData: Dispatch<SetStateAction<Tendering>>;
}

const SortableRow: React.FC<{ id: number; children: (handle: React.ReactNode) => React.ReactNode }> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const handle = (
    <ReOrderDotsVerticalRegular
      {...attributes}
      {...listeners}
      title="Dra för att ändra rangordning"
      style={{ cursor: 'grab', touchAction: 'none' }}
    />
  );

  return (
    <TableRow
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        position: 'relative',
        zIndex: isDragging ? 1 : 0,
        background: isDragging ? tokens.colorNeutralBackground1Hover : undefined,
      }}
    >
      {children(handle)}
    </TableRow>
  );
};

interface RankingViewProps extends TenderingAdminRankingProps {
  // Draft rankings are stored separately and take effect after indexing is done
  draft?: boolean;
}

const RankingView: React.FC<RankingViewProps> = ({
  tenderingDocument,
  tenders,
  draft = false
}) => {
  const rankingsKey = draft ? 'draftRankings' : 'rankings';
  const rankingTypeKey = draft ? 'draftRankingType' : 'rankingType';
  const [selectedPart, setSelectedPart] = useState<TenderingPart | null>(null);
  const [tenderList, setTenderList] = useState<Tender[]>([]);

  const tenderProperties = ["content.tenderAnswers", "content.tenderingParts"];

  // Draft prices include indexings that have opened for requests but haven't taken effect yet
  const pendingIndexings = useMemo(() => {
    if (!draft) return [];
    const today = getTodayKey();
    return (tenderingDocument.indexing || [])
      .filter(i => i.indexingDate
        && toDateKey(i.indexingDate) > today
        && (!i.startDate || toDateKey(i.startDate) <= today))
      .sort((a, b) => toDateKey(a.indexingDate).localeCompare(toDateKey(b.indexingDate)));
  }, [draft, tenderingDocument.indexing]);

  // Only tenders that requested the indexing get it applied
  const getIndexedValue = (tender: Tender, questionID: number, value: any): number => {
    const multiplier = pendingIndexings
      .filter(i => tender.indexing?.some(t => Number(t.indexingID) === i.indexingID)
        && i.prices?.some(p => p.questionID === questionID))
      .reduce((total, i) => total * (i.index ?? 1), 1);
    return Number((Number(value) * multiplier).toFixed(2));
  };

  const getEvaluatedParts =(): TenderingPart[] => {
    return tenderingDocument.tenderingParts.filter(p => p.evaluated);
  };

  const getInitPrice = (tender: Tender): number => {
    if (!selectedPart) return 0;
    const tenderingPart = tender.tenderingParts.find(tp => tp.tenderingPartID === selectedPart.tenderingPartID);
    return tenderingPart?.priceInit || 0;
  };

  const getExtPrice = (tender: Tender): number => {
    if (!selectedPart) return 0;
    const tenderingPart = tender.tenderingParts.find(tp => tp.tenderingPartID === selectedPart.tenderingPartID);
    return tenderingPart?.priceExt || 0;
  };

  const onPartChange = (partId: string) => {
    const part = getEvaluatedParts().find(p => p.tenderingPartID.toString() === partId);
    if (!part) return;

    setSelectedPart(part);
    let updatedTenderList: Tender[] = deepCloneObject(tenders);

    updatedTenderList.forEach(tender => {
      let price: any;
      
      // If double price
      if (typeof part.ranking === 'object' && part.ranking !== null) {
        const price1 = tender.tenderAnswers?.find(a => a.questionID === part.ranking.prices[0]);
        const price2 = tender.tenderAnswers?.find(a => a.questionID === part.ranking.prices[1]);
        if (price1 && price2) {
          const priceInit = getIndexedValue(tender, price1.questionID, price1.value);
          const priceExt = getIndexedValue(tender, price2.questionID, price2.value);
          price = {
            priceInit,
            priceExt,
            value: (priceInit * 12) + (priceExt * 24)
          };
        }
      } else {
        price = tender.tenderAnswers?.find(a => a.questionID === part.ranking);
        if (price) {
          price = { ...price, value: getIndexedValue(tender, price.questionID, price.value) };
        }
      }

      if (price) {
        tender.tenderingParts.forEach(tpart => {
          if (part.tenderingPartID === tpart.tenderingPartID) {
            tpart.price = Number(price.value);
            tpart.rankingValue = Number(price.rankingValue);
            if (price.priceInit && price.priceExt) {
              tpart.priceInit = Number(price.priceInit);
              tpart.priceExt = Number(price.priceExt);
            }
          }
        });
      }
    });

    updatedTenderList = updatedTenderList.filter(tl => {
      let found = false;
      for (const tp of tl.tenderingParts) {
        if (tp.price > 0 && tp.tenderingPartID === part.tenderingPartID) {
          found = true;
          tl.price = Number(tp.price);
          tl.rankingValue = Number(tp.rankingValue);
          break;
        }
      }
      return found;
    });

    if (part[rankingsKey]) {
      updatedTenderList = setTenderListRankings(updatedTenderList, part[rankingsKey]);
      updatedTenderList = sortByRank(updatedTenderList);
    }

    setTenderList(updatedTenderList);
  };

  const rankByPrice = () => {
    if (!selectedPart) return;
    
    selectedPart[rankingsKey] = setTenderingPartRankingByPrice(tenderList);
    selectedPart[rankingTypeKey] = RankingType.Price;
    onPartChange(selectedPart.tenderingPartID.toString());
  };

  const rankByPreIndexingPrice = () => {
    if (!selectedPart) return;
    
    selectedPart[rankingsKey] = setTenderingPartRankingByRankingValue(tenderList);
    selectedPart[rankingTypeKey] = RankingType.RankingValue;
    onPartChange(selectedPart.tenderingPartID.toString());
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!selectedPart?.[rankingsKey] || !over || active.id === over.id) return;

    const ids = tenderList.map(t => t.tenderID);
    const from = ids.indexOf(active.id as number);
    const to = ids.indexOf(over.id as number);
    if (from < 0 || to < 0) return;

    ids.splice(to, 0, ids.splice(from, 1)[0]);
    ids.forEach((id, index) => {
      const ranking = selectedPart[rankingsKey].find((r: any) => r.tenderID === id);
      if (ranking) ranking.rank = index + 1;
    });
    onPartChange(selectedPart.tenderingPartID.toString());
  };

  const getDisplayedColumns = () => {
    if (!selectedPart) return [];
    
    let columns = ['tenderName'];
    
    if (selectedPart[rankingTypeKey] === RankingType.RankingValue) {
      columns.push('rankingValue');
    }
    
    if (typeof selectedPart.ranking === 'object' && selectedPart.ranking !== null) {
      columns.push('priceInit', 'priceExt');
    } else {
      columns.push('price');
    }
    
    columns.push('rank', 'action');
    return columns;
  };

  const evaluatedParts = useMemo(() => getEvaluatedParts(), [tenderingDocument.tenderingParts]);
  const displayedColumns = useMemo(() => getDisplayedColumns(), [selectedPart]);

  return (
    <Card>
      <CardPreview>
        <Title3>{draft ? 'Utkast till rangordning' : 'Rangordningar'}</Title3>
      </CardPreview>
      <div className="overflow-auto p-4">
        {draft && (
          <MessageBar className="mb-4">
            <MessageBarBody>
              Utkastet påverkar inte nuvarande rangordning. Det träder i kraft när indexeringen är klar.
              {pendingIndexings.length > 0
                ? ` Priserna är beräknade med kommande indexering: ${pendingIndexings.map(i => `${i.indexingName} (×${i.index})`).join(', ')}.`
                : ' Ingen aktiv indexering – priserna är nuvarande priser.'}
            </MessageBarBody>
          </MessageBar>
        )}
        <div className="mb-6 flex flex-col gap-4">
          <Field>
            <Label>Välj delområde</Label>
            <Dropdown
              placeholder="Välj Delområde"
              onOptionSelect={(_, data) => onPartChange(data.optionValue || '')}
              style={{ minWidth: '25%', maxWidth: '100%' }}
              >
              {evaluatedParts.map((part) => (
                <Option key={part.tenderingPartID} value={part.tenderingPartID.toString()}>
                  {part.tenderingPart}
                </Option>
              ))}
            </Dropdown>
          </Field>
          
          {selectedPart && (
            <div className="flex gap-4 mt-4">
              <Button appearance="primary" onClick={rankByPrice}>
                Rangordna på pris
              </Button>
              <Button appearance="primary" onClick={rankByPreIndexingPrice}>
                Rangordna på orginal pris
              </Button>
            </div>
          )}
        </div>
  
        {selectedPart && tenderList.length > 0 && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <Table className="w-full" aria-label="Tendering ranking table">
            <TableHeader>
              <TableRow>
                {displayedColumns.includes('tenderName') && (
                  <TableHeaderCell>Verksamhet</TableHeaderCell>
                )}
                {displayedColumns.includes('rankingValue') && (
                  <TableHeaderCell>Orginal Pris</TableHeaderCell>
                )}
                {displayedColumns.includes('price') && (
                  <TableHeaderCell>Pris</TableHeaderCell>
                )}
                {displayedColumns.includes('priceInit') && (
                  <TableHeaderCell>Initialt Pris</TableHeaderCell>
                )}
                {displayedColumns.includes('priceExt') && (
                  <TableHeaderCell>Förlängningspris</TableHeaderCell>
                )}
                {displayedColumns.includes('rank') && (
                  <TableHeaderCell>Rangordning</TableHeaderCell>
                )}
                {displayedColumns.includes('action') && (
                  <TableHeaderCell></TableHeaderCell>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              <SortableContext items={tenderList.map(t => t.tenderID)} strategy={verticalListSortingStrategy}>
              {tenderList.map((tender) => (
                <SortableRow key={tender.tenderID} id={tender.tenderID}>
                {(handle) => (
                <>
                  {displayedColumns.includes('tenderName') && (
                    <TableCell>
                      <TableCellLayout>{tender.tenderName}</TableCellLayout>
                    </TableCell>
                  )}
                  {displayedColumns.includes('rankingValue') && (
                    <TableCell>
                      <TableCellLayout>{tender.rankingValue}</TableCellLayout>
                    </TableCell>
                  )}
                  {displayedColumns.includes('price') && (
                    <TableCell>
                      <TableCellLayout>{tender.price}</TableCellLayout>
                    </TableCell>
                  )}
                  {displayedColumns.includes('priceInit') && (
                    <TableCell>
                      <TableCellLayout>
                        {getInitPrice(tender)} <em>SEK</em>
                      </TableCellLayout>
                    </TableCell>
                  )}
                  {displayedColumns.includes('priceExt') && (
                    <TableCell>
                      <TableCellLayout>
                        {getExtPrice(tender)} <em>SEK</em>
                      </TableCellLayout>
                    </TableCell>
                  )}
                  {displayedColumns.includes('rank') && (
                    <TableCell>
                      <TableCellLayout>{tender.rank}</TableCellLayout>
                    </TableCell>
                  )}
                  {displayedColumns.includes('action') && (
                    <TableCell>
                      <TableCellLayout>{handle}</TableCellLayout>
                    </TableCell>
                  )}
                </>
                )}
                </SortableRow>
              ))}
              </SortableContext>
            </TableBody>
          </Table>
          </DndContext>
        )}
      </div>
    </Card>
  );
};

export const TenderingRanking: React.FC<TenderingAdminRankingProps> = (props) => {
  const [tab, setTab] = useState<'current' | 'draft'>('current');

  return (
    <div className="flex flex-col gap-4">
      <TabList selectedValue={tab} onTabSelect={(_, data) => setTab(data.value as 'current' | 'draft')}>
        <Tab value="current">Rangordning</Tab>
        <Tab value="draft">Utkast (efter indexering)</Tab>
      </TabList>
      <RankingView key={tab} {...props} draft={tab === 'draft'} />
    </div>
  );
};

export default TenderingRanking;
